import { useEffect, useState, useCallback } from 'react';
import { Tickets, type AttachmentRecord } from '../lib/api';
import { uploadAttachmentFile, getAttachmentDownloadURL } from '../lib/storage';
import { confirmDialog, alertDialog } from './ui/confirm';

interface Props {
  ticketId: number;
  attachments: AttachmentRecord[];
  onChange: () => void;
}

interface UploadInProgress {
  id: string;
  filename: string;
  pct: number;
  error?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isImage(mime: string): boolean {
  return mime.startsWith('image/');
}

export function Attachments({ ticketId, attachments, onChange }: Props) {
  const [uploads, setUploads] = useState<UploadInProgress[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  // Lazily resolve download URLs for image thumbnails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const a of attachments) {
        if (isImage(a.mimeType) && !thumbnails[a.id]) {
          try {
            const url = await getAttachmentDownloadURL(a.storagePath);
            if (!cancelled) setThumbnails((t) => ({ ...t, [a.id]: url }));
          } catch {
            // ignore
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const handleFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const tempId = crypto.randomUUID();
        setUploads((u) => [...u, { id: tempId, filename: file.name, pct: 0 }]);
        try {
          const { storagePath } = await uploadAttachmentFile(ticketId, file, file.name, (pct) => {
            setUploads((u) => u.map((x) => (x.id === tempId ? { ...x, pct } : x)));
          });
          await Tickets.confirmAttachment(ticketId, {
            storagePath,
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
          });
          setUploads((u) => u.filter((x) => x.id !== tempId));
          onChange();
        } catch (e) {
          setUploads((u) =>
            u.map((x) =>
              x.id === tempId
                ? { ...x, error: e instanceof Error ? e.message : 'Upload failed' }
                : x,
            ),
          );
        }
      }
    },
    [ticketId, onChange],
  );

  // Paste-from-clipboard: catches Ctrl-V image data anywhere on the page when the area is mounted.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (!e.clipboardData) return;
      const files: File[] = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void handleFiles(files);
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFiles]);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) void handleFiles(files);
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) void handleFiles(files);
    e.target.value = '';
  }

  async function remove(id: string) {
    const ok = await confirmDialog({
      title: 'Delete attachment?',
      body: 'This attachment will be removed from the ticket.',
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await Tickets.removeAttachment(ticketId, id);
      onChange();
    } catch (e) {
      await alertDialog({ title: 'Delete failed', body: e instanceof Error ? e.message : 'Delete failed' });
    }
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver ? 'border-brand-500 bg-brand-50' : 'border-slate-300 bg-white'
        }`}
      >
        <p className="text-sm text-slate-600">
          Drop files here, or{' '}
          <label className="text-brand-600 hover:text-brand-700 cursor-pointer underline">
            browse
            <input type="file" multiple onChange={onFilePick} className="hidden" />
          </label>
          .
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Or paste an image with Ctrl-V (Cmd-V on Mac).
        </p>
      </div>

      {uploads.length > 0 && (
        <ul className="space-y-1">
          {uploads.map((u) => (
            <li key={u.id} className="text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <div className="flex justify-between items-center">
                <span className="truncate">{u.filename}</span>
                <span className="text-xs text-slate-500">
                  {u.error ? <span className="text-red-600">{u.error}</span> : `${Math.round(u.pct * 100)}%`}
                </span>
              </div>
              {!u.error && (
                <div className="h-1 bg-slate-200 rounded mt-1 overflow-hidden">
                  <div className="h-full bg-brand-500" style={{ width: `${u.pct * 100}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {attachments.length === 0 && uploads.length === 0 ? (
        <p className="text-sm text-slate-500">No attachments yet.</p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {attachments.map((a) => (
            <li key={a.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
              {isImage(a.mimeType) && thumbnails[a.id] ? (
                <a href={thumbnails[a.id]} target="_blank" rel="noreferrer">
                  <img src={thumbnails[a.id]} alt={a.filename} className="w-full h-32 object-cover" />
                </a>
              ) : (
                <div className="w-full h-32 bg-slate-50 flex items-center justify-center text-slate-500 text-xs">
                  {a.mimeType.split('/')[1]?.toUpperCase() ?? 'FILE'}
                </div>
              )}
              <div className="p-2 text-xs space-y-1">
                <div className="truncate font-medium text-slate-700" title={a.filename}>
                  {a.filename}
                </div>
                <div className="flex items-center justify-between text-slate-500">
                  <span>{formatSize(a.sizeBytes)}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        const url = thumbnails[a.id] ?? (await getAttachmentDownloadURL(a.storagePath));
                        window.open(url, '_blank');
                      }}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      Download
                    </button>
                    <button onClick={() => remove(a.id)} className="text-slate-500 hover:text-red-600">
                      ✕
                    </button>
                  </div>
                </div>
                <div className="text-slate-500 truncate">by {a.uploader.name}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
